/**
 * 3-D layer viewer, opened in a dialog.
 *
 * The viewer is a separate application on another origin. Two things follow
 * from that, and both are handled here rather than in Liquid:
 *
 * 1. **It is expensive.** The iframe's `src` is withheld until the dialog is
 *    opened, so a product page costs nothing extra for a viewer nobody taps.
 *    Once loaded it stays loaded — reopening is instant.
 *
 * 2. **We cannot see inside it.** Nothing in the frame can be measured or
 *    styled from here, so the loading message is dismissed on the iframe's own
 *    `load` event, which is the only signal this side actually gets.
 *
 * Wrapped in a guard because two sections can now emit <product-3d> on one
 * page — the standalone viewer section and the merged story-and-viewer band.
 * The browser fetches a `<script src>` once but *executes* every occurrence,
 * so an unguarded top-level class is redeclared and throws.
 */

(() => {
  if (customElements.get('product-3d')) return;

class Product3D extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-3d-modal]');
    this.frame = this.querySelector('[data-3d-frame]');
    this.openBtn = this.querySelector('[data-3d-open]');
    this.loading = this.querySelector('[data-3d-loading]');

    if (!this.modal || !this.frame || !this.openBtn) return;

    // Without dialog support the button would open nothing. Sending the
    // visitor to the viewer in a new tab is worse than a modal and far better
    // than a dead control.
    if (typeof this.modal.showModal !== 'function') {
      const url = this.dataset.url;
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.className = this.openBtn.className;
        link.textContent = this.openBtn.textContent;
        this.openBtn.replaceWith(link);
      }
      return;
    }

    this.openBtn.addEventListener('click', () => this.open());

    this.frame.addEventListener('load', () => {
      if (this.loading) this.loading.hidden = true;
    });

    // The page behind a full-screen dialog should not scroll under it.
    this.modal.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
    });
  }

  open() {
    if (!this.frame.src) {
      const url = this.frame.dataset.src;
      if (url) this.frame.src = url;
    }

    this.modal.showModal();
    document.documentElement.style.overflow = 'hidden';
  }
}

customElements.define('product-3d', Product3D);

  /**
   * Triggers that live outside the <product-3d> element.
   *
   * The pill beside the product title is rendered by `main-product`, while the
   * viewer and its dialog belong to a different section further down the page,
   * so the element cannot find that trigger with a querySelector. Delegating
   * from the document covers any number of remote triggers without either
   * section needing to know the other exists.
   *
   * Each trigger is a real link to the viewer, so this only takes over when it
   * can do better: with no <product-3d> on the page, or no dialog support, the
   * click is left alone and the link opens the viewer in a new tab.
   */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-3d-open-remote]');
    if (!trigger) return;

    const viewer = document.querySelector('product-3d');
    if (!viewer || typeof viewer.open !== 'function') return;
    if (typeof viewer.modal?.showModal !== 'function') return;

    event.preventDefault();
    viewer.open();
  });
})();
